'use client';

import {
  Button,
  Heading,
  Inline,
  Input,
  Stack,
  Text,
  TextArea,
} from '@cortex/ui';
import { useState } from 'react';

import { QUESTION_ENTER_ANIMATION } from './styles';
import type { TextQuestionProps } from './types';

export function TextQuestion({
  acknowledgment,
  question,
  placeholder = 'Type your answer here...',
  maxLength,
  onSubmit,
}: TextQuestionProps) {
  const [text, setText] = useState('');

  // Use textarea for longer responses (when maxLength > 150 or not set)
  const useTextarea = !maxLength || maxLength > 150;

  const handleSubmit = () => {
    if (text.trim()) {
      onSubmit(text.trim());
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (e.key === 'Enter' && !useTextarea && text.trim()) {
      e.preventDefault();
      handleSubmit();
    }
    // For textarea, Ctrl/Cmd+Enter submits
    if (
      e.key === 'Enter' &&
      useTextarea &&
      (e.ctrlKey || e.metaKey) &&
      text.trim()
    ) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const remainingChars = maxLength ? maxLength - text.length : undefined;

  return (
    <Stack gap="lg" className={QUESTION_ENTER_ANIMATION}>
      {acknowledgment && (
        <Text color="muted" size="sm" className="italic">
          {acknowledgment}
        </Text>
      )}

      <Heading level={3} className="text-lg font-medium">
        {question}
      </Heading>

      <Stack gap="sm">
        {useTextarea ? (
          <TextArea
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={placeholder}
            {...(maxLength !== undefined && { maxLength })}
            onKeyDown={handleKeyDown}
            className="min-h-32 resize-y"
            autoFocus
          />
        ) : (
          <Input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={placeholder}
            maxLength={maxLength}
            onKeyDown={handleKeyDown}
            autoFocus
          />
        )}

        <Inline justify="between" align="center">
          {maxLength && (
            <Text
              as="span"
              size="xs"
              color={
                remainingChars === 0
                  ? 'error'
                  : remainingChars !== undefined && remainingChars < 20
                    ? 'warning'
                    : 'muted'
              }
              className="transition-colors"
            >
              {remainingChars} characters remaining
            </Text>
          )}
          {useTextarea && (
            <Text as="span" color="muted" size="xs" className="ml-auto">
              Press {navigator.platform.includes('Mac') ? 'Cmd' : 'Ctrl'}+Enter
              to submit
            </Text>
          )}
        </Inline>
      </Stack>

      <Button
        onClick={handleSubmit}
        disabled={!text.trim()}
        size="lg"
        className="w-full"
      >
        Continue
      </Button>
    </Stack>
  );
}
